import { useAuth0 } from '@auth0/auth0-react'
import { ChevronRight } from '@mui/icons-material'
import {
  Box,
  Button,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  useTheme,
} from '@mui/material'
import React, { useContext, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  ChevBackIcon,
  LocationIcon,
  PhotoIcon,
  SettingsIcon,
} from '../../assets/icons/icons'
import { DialogComponentProps } from '../../components/Dialog'
import { Fetching } from '../../components/Fetching'
import SnackBarContext from '../../context/SnackBarContext'
import { formatDate, formatTime, formatTimes } from '../../lib/time'
import { CostCodesGrid } from './cost-codes/CostCodesGrid'
import { useGetCostCodes, useProcessCostCodes } from './cost-codes/hooks'
import { ListDailyReports } from './daily-reports/ListDailyReports'
import { useGetProject, useGetProjectsAll } from './hooks'
import { ListTNMReports } from './time-materials/ListTNMReports'
import { ListTimesheets } from './timesheets/ListTimesheets'
import { useGetTimeSheets, useMutateUpdateTimeSheet } from './timesheets/hooks'
import ProjectContracts from './contracts/ProjectContracts'
import { columns as costcodeColumns } from './cost-codes/ListColumns'

export const Project = (props: any) => {
  const { id } = useParams()
  const { isLoading, user } = useAuth0()
  const {
    data,
    isFetching,
    refetch: refetchProjectDetails,
  } = useGetProject('project', id)
  const theme = useTheme()
  const [selectedTab, setSelectedTab] = useState('Timesheets')
  const [showMore, setShowMore] = useState(false)

  const [exportAsCSV, setExportAsCSV] = useState<boolean | null>(null)
  const bodyData = {
    page: 1,
    pageSize: 50,
  }
  const { data: costCodes, refetch: refetchCostCodes } = useGetCostCodes(
    'costCodes',
    id,
    null,
    bodyData
  )
  const [openExportType, setOpenExportType] = useState<null | HTMLElement>(null)
  const [openProject, setOpenProject] = useState<null | HTMLElement>(null)
  const { data: allProjects } = useGetProjectsAll('projects')
  const { mutateAsync: uploadCostCodes } = useProcessCostCodes('costcodes', id)
  const [userRoles, setUserRoles] = useState<
    'vtadmin' | 'superintendent' | 'worker' | null
  >(null)
  const navigate = useNavigate()
  const ts = Date.now()
  const { toggleSnackbar } = useContext(SnackBarContext)
  const [isOpen, setIsOpen] = useState<boolean | undefined>(false)
  const [errorMessage, setErrorMessage] = useState<string | undefined>('')
  const { mutateAsync } = useProcessCostCodes('costcodes', id)
  const dialogProps = {
    title: 'File was not uploaded due to error: ',
    isOpen: isOpen,
    handleConfirmation: () => {
      setIsOpen(false)
    },
    formIsValid: true,
    acceptButtonText: 'Accept',
  } as DialogComponentProps

  const getInvalidIndices = (uploadResult: any): number[] => {
    return uploadResult?.invalidArrays?.map((item: any) => item.index)
  }

  const [value, setValue] = React.useState(0)

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue)
  }

  const handleImportData = async (costCodeData: unknown) => {
    const uploadResult = await mutateAsync(costCodeData)
    const invalidIndices = getInvalidIndices(uploadResult)

    if (uploadResult.isValid === false) {
      const errorMessage = `Invalid entry found on line(s):${invalidIndices}`
      setErrorMessage(errorMessage)
      setIsOpen(true)
      return
    }

    if (uploadResult) {
      navigate(`/projects/${id}/cost-codes`)
      toggleSnackbar('Cost codes uploaded successfully', 'success')
    }
  }

  useEffect(() => {
    refetchCostCodes()
    refetchProjectDetails()
    if (user) {
      setUserRoles(user[`${process.env.REACT_APP_WEB_URL}/roles`])
    }
  }, [user])

  if (isLoading || isFetching) {
    return <Fetching />
  }

  return (
    <Box sx={{ padding: '0 16px' }}>
      {data ? (
        <Box sx={{ marginTop: 1, marginLeft: 2, marginRight: 2 }}>
          <IconButton
            sx={{
              background: '#24212C12',
              borderRadius: 1.8,
              padding: '6px 10px',
            }}
            onClick={() => navigate('/projects')}>
            <ChevBackIcon />{' '}
            <p
              style={{
                fontSize: 12,
                paddingTop: 0,
                paddingRight: 0,
                paddingBottom: 0,
                margin: 0,
                paddingLeft: 7,
              }}>
              Go Back
            </p>
          </IconButton>
          <IconButton
            sx={{
              background: '#24212C21',
              borderRadius: 1.8,
              padding: '2px 10px',
              marginLeft: 1,
            }}
            id='basic-button-1'
            aria-controls={Boolean(openProject) ? 'basic-menu-2' : undefined}
            aria-haspopup='true'
            aria-expanded={Boolean(openProject) ? 'true' : undefined}
            onClick={(event: any) => setOpenProject(event.currentTarget)}>
            <p
              style={{
                fontSize: 12,
                paddingTop: 0,
                paddingRight: 0,
                paddingBottom: 0,
                margin: 0,
                paddingLeft: 7,
              }}>
              Select Project
            </p>
            <ChevronRight
              sx={{ transform: 'rotate(90deg)', color: '#5A525B' }}
            />
          </IconButton>
          <Menu
            id='basic-menu-2'
            anchorEl={openProject}
            open={Boolean(openProject)}
            onClose={() => setOpenProject(null)}
            MenuListProps={{
              'aria-labelledby': 'basic-button-1',
            }}
            sx={{
              boxShadow: 'none',
              '& .MuiPaper-root': {
                boxShadow:
                  '0px 5px 12px 0px #0000000A, 0px 22px 22px 0px #00000008, 0px 49px 30px 0px #00000005, 0px 88px 35px 0px #00000003, 0px 137px 38px 0px #00000000',
                borderRadius: '10px',
                border: '1px solid #F1EFF5',
              },
              '& ul': {
                padding: '7px 0px',
                // backgroundColor: "red",
                boxShadow: 'none',
              },
              '& li:hover': {
                backgroundColor: 'transparent',
              },
              '& li': {
                margin: '5px 17px',
                borderBottom: '1px solid #DEE2E0',
                textAlign: 'left',
                padding: '5px 0',
                fontSize: 14,
                color: '#5F5C5F',
              },
              '& li:last-child': {
                border: 'none !important',
              },
              marginTop: 1,
            }}>
            {allProjects?.map((item: any) => (
              <MenuItem
                sx={{ width: 180 }}
                onClick={() => {
                  setOpenProject(null)
                  navigate('/projects/' + item.id)
                }}>
                {item?.projectName}
              </MenuItem>
            ))}
          </Menu>
          <Grid
            container
            spacing={{ md: 1, xs: 2, sm: 2, lg: 2 }}
            alignItems={'center'}
            justifyContent={'space-between'}
            display={'flex'}>
            <Grid item xs={12} sm={12} md={6} lg={7} sx={{ marginTop: 2 }}>
              <Typography
                variant='h4'
                style={{
                  fontWeight: '600',
                  fontFamily: 'Rza',
                  padding: 0,
                  margin: 0,
                }}>
                {data.projectName}
              </Typography>
              <p style={{ margin: 0, padding: 0 }}>
                Tracking Time for Employees of Black N Rounds ABC
              </p>
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              md={3}
              lg={2.5}
              alignItems={'center'}
              justifyContent={'center'}
              display={'flex'}>
              <Button
                variant='contained'
                sx={{
                  padding: theme.spacing(1.5),
                  borderRadius: '8px',
                  textTransform: 'capitalize',
                  width: '100%',
                  fontSize: '13px',
                  backgroundColor: '#59A0A0',
                  '&:hover': {
                    backgroundColor: '#59A0A0',
                  },
                }}
                startIcon={<SettingsIcon />}
                onClick={() => navigate('edit')}>
                Project Settings
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3} lg={2.5}>
              <Button
                variant='contained'
                sx={{
                  padding: theme.spacing(1.5),
                  borderRadius: '8px',
                  textTransform: 'capitalize',
                  width: '100%',
                  fontSize: '13px',
                }}
                startIcon={<PhotoIcon />}
                onClick={() => navigate('photos')}>
                Photos
              </Button>
            </Grid>
          </Grid>
          <Box
            marginTop={3}
            display='flex'
            alignItems='center'
            justifyContent={'center'}
            sx={{
              width: '100%',
              backgroundColor: 'white',
              padding: 2,
              boxShadow: '0px 2px 4px 0px #0000000D',
              borderRadius: '8px',
            }}>
            <Grid sx={{ width: '100%' }}>
              <Box
                alignItems={'center'}
                justifyContent={'center'}
                display={'flex'}
                sx={{ padding: '10px 2px', borderBottom: '1px solid #D7D4DE' }}>
                <Typography sx={{ color: '#7C7A80', width: '100%' }}>
                  Client Name
                </Typography>
                <Typography sx={{ color: '#2B262C', width: '100%' }}>
                  {data?.clientName}
                </Typography>
              </Box>
              <Box
                alignItems={'flex-start'}
                justifyContent={'center'}
                display={'flex'}
                sx={{ padding: '10px 2px', borderBottom: '1px solid #D7D4DE' }}>
                <Typography sx={{ color: '#7C7A80', width: '100%' }}>
                  Client Email
                </Typography>
                <Box style={{ width: '100%' }}>
                  <Typography
                    sx={{
                      color: '#2B262C',
                      width: '100%',
                      textDecorationLine: 'underline',
                      textDecoration: 'underline',
                      textDecorationColor: '#F0845D',
                      textUnderlineOffset: '5px',
                    }}>
                    {data?.clientEmails}
                  </Typography>
                </Box>
              </Box>
              <Box
                alignItems={'center'}
                justifyContent={'center'}
                display={'flex'}
                sx={{ padding: '10px 2px', borderBottom: '1px solid #D7D4DE' }}>
                <Typography sx={{ color: '#7C7A80', width: '100%' }}>
                  Service
                </Typography>
                <Typography sx={{ color: '#2B262C', width: '100%' }}>
                  {data?.service}
                </Typography>
              </Box>

              <Box
                alignItems={'center'}
                justifyContent={'center'}
                display={'flex'}
                sx={{
                  padding: '10px 2px',
                  borderBottom: !showMore ? 'none ' : '1px solid #D7D4DE',
                }}>
                <Box
                  sx={{ width: '100%' }}
                  display='flex'
                  justifyContent={'space-between'}
                  flexDirection={'column'}
                  alignItems={'flex-start'}>
                  <Typography sx={{ color: '#7C7A80', width: '100%' }}>
                    Address(es)
                  </Typography>
                  <IconButton
                    sx={{
                      background: '#F1EFF5',
                      borderRadius: '10px',
                      padding: '8px 10px',
                      marginTop: 1,
                    }}
                    onClick={() => {
                      const lat = data?.locations[0].latitude
                      const long = data?.locations[0].longitude
                      const url = `https://www.google.com/maps/search/?api=1&query=${lat},${long}`
                      window.open(url, '_blank')
                    }}>
                    <LocationIcon />{' '}
                    <p
                      style={{
                        fontSize: 14,
                        paddingTop: 0,
                        paddingRight: 0,
                        paddingBottom: 0,
                        margin: 0,
                        fontWeight: '500',
                        paddingLeft: 7,
                        color: 'black',
                      }}>
                      View on map
                    </p>
                  </IconButton>
                </Box>
                <Typography sx={{ color: '#2B262C', width: '100%' }}>
                  {data?.locations?.map((item: any) => {
                    return (
                      <>
                        {item?.address}
                        <br />
                      </>
                    )
                  })}
                </Typography>
              </Box>
              {/* Show More Data */}
              {showMore && (
                <>
                  <Box
                    alignItems={'center'}
                    justifyContent={'center'}
                    display={'flex'}
                    sx={{
                      padding: '10px 2px',
                      borderBottom: '1px solid #D7D4DE',
                    }}>
                    <Typography sx={{ color: '#7C7A80', width: '100%' }}>
                      Project Time Zone
                    </Typography>
                    <Typography sx={{ color: '#2B262C', width: '100%' }}>
                      {data?.projectTimeZone ?? '---'}
                    </Typography>
                  </Box>
                  <Box
                    alignItems={'center'}
                    justifyContent={'center'}
                    display={'flex'}
                    sx={{
                      padding: '10px 2px',
                      borderBottom: '1px solid #D7D4DE',
                    }}>
                    <Typography sx={{ color: '#7C7A80', width: '100%' }}>
                      Project Number
                    </Typography>
                    <Typography sx={{ color: '#2B262C', width: '100%' }}>
                      {data?.projectNumber}
                    </Typography>
                  </Box>
                  <Box
                    alignItems={'center'}
                    justifyContent={'center'}
                    display={'flex'}
                    sx={{
                      padding: '10px 2px',
                      borderBottom: '1px solid #D7D4DE',
                    }}>
                    <Typography sx={{ color: '#7C7A80', width: '100%' }}>
                      Project Due
                    </Typography>
                    <Typography sx={{ color: '#2B262C', width: '100%' }}>
                      {formatDate(data?.projectDueDate)}
                    </Typography>
                  </Box>

                  <Box
                    alignItems={'center'}
                    justifyContent={'center'}
                    display={'flex'}
                    sx={{
                      padding: '10px 2px',
                      borderBottom: '1px solid #D7D4DE',
                    }}>
                    <Typography sx={{ color: '#7C7A80', width: '100%' }}>
                      Daily End Time
                    </Typography>
                    <Typography sx={{ color: '#2B262C', width: '100%' }}>
                      {formatTime(data?.dailyEndTime)}
                    </Typography>
                  </Box>

                  <Box
                    alignItems={'center'}
                    justifyContent={'center'}
                    display={'flex'}
                    sx={{
                      padding: '10px 2px',
                      borderBottom: '1px solid #D7D4DE',
                    }}>
                    <Typography sx={{ color: '#7C7A80', width: '100%' }}>
                      Daily Start Time
                    </Typography>
                    <Typography sx={{ color: '#2B262C', width: '100%' }}>
                      {formatTime(data?.dailyStartTime)}
                    </Typography>
                  </Box>
                  <Box
                    alignItems={'center'}
                    justifyContent={'center'}
                    display={'flex'}
                    sx={{
                      padding: '10px 2px',
                      borderBottom: '1px solid #D7D4DE',
                    }}>
                    <Typography sx={{ color: '#7C7A80', width: '100%' }}>
                      Automatic Clock Out
                    </Typography>
                    <Typography sx={{ color: '#2B262C', width: '100%' }}>
                      {data?.automaticClockOut?.enabled ? 'Yes' : 'No'}
                    </Typography>
                  </Box>
                  <Box
                    alignItems={'center'}
                    justifyContent={'center'}
                    display={'flex'}
                    sx={{
                      padding: '10px 2px',
                    }}>
                    <Typography sx={{ color: '#7C7A80', width: '100%' }}>
                      Automatic Clock Out Time
                    </Typography>
                    <Typography sx={{ color: '#2B262C', width: '100%' }}>
                      {data?.automaticClockOut?.enabled === true
                        ? formatTimes(data?.automaticClockOut?.time)
                        : '---'}
                    </Typography>
                  </Box>
                </>
              )}
            </Grid>
            <Box style={{ width: 60 }} />
            <Grid sx={{ width: '100%' }}>
              <Box
                alignItems={'center'}
                justifyContent={'center'}
                display={'flex'}
                sx={{ padding: '10px 2px', borderBottom: '1px solid #D7D4DE' }}>
                <Typography sx={{ color: '#7C7A80', width: '100%' }}>
                  Total Budgeted Hours
                </Typography>
                <Typography sx={{ color: '#2B262C', width: '100%' }}>
                  {data?.totalBudgetedHours}
                </Typography>
              </Box>
              <Box
                alignItems={'center'}
                justifyContent={'center'}
                display={'flex'}
                sx={{ padding: '10px 2px', borderBottom: '1px solid #D7D4DE' }}>
                <Typography sx={{ color: '#7C7A80', width: '100%' }}>
                  Total Worked Hours
                </Typography>
                <Typography sx={{ color: '#2B262C', width: '100%' }}>
                  {data?.totalWorkedHoursFormatted}
                </Typography>
              </Box>
              <Box
                alignItems={'center'}
                justifyContent={'center'}
                display={'flex'}
                sx={{ padding: '10px 2px', borderBottom: '1px solid #D7D4DE' }}>
                <Typography sx={{ color: '#7C7A80', width: '100%' }}>
                  Total Remaining Hours
                </Typography>
                <Typography sx={{ color: '#2B262C', width: '100%' }}>
                  {data?.totalRemainingHours}
                </Typography>
              </Box>
              <Box
                alignItems={'center'}
                justifyContent={'center'}
                display={'flex'}
                sx={{
                  padding: '10px 2px',
                  borderBottom: !showMore ? 'none ' : '1px solid #D7D4DE',
                }}>
                <Box
                  sx={{ width: '100%' }}
                  display='flex'
                  justifyContent={'space-between'}
                  flexDirection={'column'}
                  alignItems={'flex-start'}>
                  <Box
                    display={'flex'}
                    alignItems={'center'}
                    justifyContent={'space-between'}
                    sx={{ width: '100%' }}>
                    <Typography sx={{ color: '#7C7A80', width: '100%' }}>
                      Total T&M Worked Hours
                    </Typography>
                    <Typography sx={{ color: '#2B262C', width: '100%' }}>
                      {data?.totalTNMWorkedHoursFormatted}
                    </Typography>
                  </Box>

                  {!showMore && (
                    <IconButton
                      sx={{
                        background: '#F1EFF5',
                        borderRadius: '10px',
                        padding: '8px 10px',
                        marginTop: 1,
                      }}
                      onClick={() => {
                        setShowMore(true)
                      }}>
                      <p
                        style={{
                          fontSize: 14,
                          paddingTop: 0,
                          paddingRight: 0,
                          paddingBottom: 0,
                          margin: 0,
                          fontWeight: '500',
                          paddingLeft: 7,
                          color: 'black',
                        }}>
                        View More Details
                      </p>
                    </IconButton>
                  )}
                </Box>
              </Box>
              {showMore && (
                <>
                  <Box
                    alignItems={'center'}
                    justifyContent={'center'}
                    display={'flex'}
                    sx={{
                      padding: '10px 2px',
                      borderBottom: '1px solid #D7D4DE',
                    }}>
                    <Typography sx={{ color: '#7C7A80', width: '100%' }}>
                      Daily Grace Time (in minutes)
                    </Typography>
                    <Typography sx={{ color: '#2B262C', width: '100%' }}>
                      {data?.dailyGraceTime}
                    </Typography>
                  </Box>
                  <Box
                    alignItems={'center'}
                    justifyContent={'center'}
                    display={'flex'}
                    sx={{
                      padding: '10px 2px',
                      borderBottom: '1px solid #D7D4DE',
                    }}>
                    <Typography sx={{ color: '#7C7A80', width: '100%' }}>
                      Lunch Break Duration
                    </Typography>
                    <Typography sx={{ color: '#2B262C', width: '100%' }}>
                      {data?.lunchDuration}
                    </Typography>
                  </Box>
                  <Box
                    alignItems={'center'}
                    justifyContent={'center'}
                    display={'flex'}
                    sx={{
                      padding: '10px 2px',
                      borderBottom: '1px solid #D7D4DE',
                    }}>
                    <Typography sx={{ color: '#7C7A80', width: '100%' }}>
                      Distance
                    </Typography>
                    <Typography sx={{ color: '#2B262C', width: '100%' }}>
                      {data?.locations?.length > 0
                        ? data?.locations[0]?.radius
                        : '---'}
                    </Typography>
                  </Box>
                  <Box
                    alignItems={'center'}
                    justifyContent={'center'}
                    display={'flex'}
                    sx={{
                      padding: '10px 2px',
                      borderBottom: '1px solid #D7D4DE',
                    }}>
                    <Typography sx={{ color: '#7C7A80', width: '100%' }}>
                      Superintendents
                    </Typography>
                    <Typography sx={{ color: '#2B262C', width: '100%' }}>
                      {data?.superintendents?.map(
                        (superintendent: any, index: number) => (
                          <Typography
                            key={index}
                            sx={{ color: '#2B262C', width: '100%' }}>
                            {`${superintendent.firstName ?? '---'} ${
                              superintendent.lastName ?? '---'
                            }`}
                          </Typography>
                        )
                      )}
                      {data?.superintendents?.length === 0 && (
                        <Typography sx={{ color: '#2B262C', width: '100%' }}>
                          ---
                        </Typography>
                      )}
                    </Typography>
                  </Box>
                  <Box
                    alignItems={'center'}
                    justifyContent={'center'}
                    display={'flex'}
                    sx={{
                      padding: '10px 2px',
                    }}>
                    <Typography sx={{ color: '#7C7A80', width: '100%' }}>
                      Kiosk Mode Enabled
                    </Typography>
                    <Typography sx={{ color: '#2B262C', width: '100%' }}>
                      {data?.kioskMode?.enabled ? 'Yes' : 'No'}
                    </Typography>
                  </Box>

                  <Box
                    alignItems={'center'}
                    justifyContent={'center'}
                    display={'flex'}
                    sx={{ padding: '10px 2px' }}>
                    <Box
                      sx={{ width: '100%' }}
                      display='flex'
                      justifyContent={'space-between'}
                      flexDirection={'column'}
                      alignItems={'flex-start'}>
                      {/* <Box
                        display={"flex"}
                        alignItems={"center"}
                        justifyContent={"space-between"}
                        sx={{ width: "100%" }}
                      >
                        <Typography sx={{ color: "#7C7A80", width: "100%" }}>
                          Cost Codes
                        </Typography>
                        <Typography
                          sx={{
                            color: "#2B262C",
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <DownloadIcon />
                          black-rounds-atx-cc.pdf
                          <Typography sx={{ color: "#5A525B80", fontSize: 14 }}>
                            62kb
                          </Typography>
                        </Typography>
                      </Box> */}
                      {showMore && (
                        <IconButton
                          sx={{
                            background: '#F1EFF5',
                            borderRadius: '10px',
                            padding: '8px 10px',
                            marginTop: 1,
                          }}
                          onClick={() => {
                            setShowMore(false)
                          }}>
                          <p
                            style={{
                              fontSize: 14,
                              paddingTop: 0,
                              paddingRight: 0,
                              paddingBottom: 0,
                              margin: 0,
                              fontWeight: '500',
                              paddingLeft: 7,
                              color: 'black',
                            }}>
                            View Less Details
                          </p>
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                </>
              )}
            </Grid>
          </Box>
          {/* <Grid container spacing={2} alignItems="stretch">
              <Grid item xs={12} sm={12} md={6} lg={6}>
                <ProjectCard project={data} />
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6}>
                <StyledCard>
                  <CardContent>
                    <GoogleMapComponent locations={data.locations} />
                  </CardContent>
                </StyledCard>
              </Grid>
            </Grid> */}
          <Box
            marginTop={3}
            display='flex'
            flexDirection={'column'}
            justifyContent={'space-between'}
            sx={{
              width: '100%',
              backgroundColor: 'white',
              padding: 2,
              boxShadow: '0px 2px 4px 0px #0000000D',
              borderRadius: '8px',
            }}>
            <Box
              display='flex'
              alignItems={'center'}
              justifyContent={'space-between'}
              marginBottom={2}>
              <Box display='flex' alignItems={'center'}>
                {[
                  'Timesheets',
                  'Daily Reports',
                  'T&M Reports',
                  'Cost Codes',
                  'Contracts',
                ].map(item => {
                  const isSelected = selectedTab === item
                  return (
                    <Box
                      sx={{
                        marginRight: 2.5,
                        cursor: 'pointer',
                        color: '#2B262C',
                      }}
                      onClick={() => {
                        setSelectedTab(item)
                        setExportAsCSV(null)
                      }}>
                      <Typography
                        sx={{
                          fontFamily: 'Rza',
                          fontSize: 20,
                          fontWeight: '500',
                          opacity: isSelected ? 1 : 0.6,
                        }}>
                        {item}
                      </Typography>
                      <Box
                        sx={{
                          backgroundColor: isSelected ? '#F0845D' : 'white',
                          width: '100%',
                          height: 1.7,
                        }}
                      />
                    </Box>
                  )
                })}
              </Box>
              <Box display='flex' alignItems={'center'}>
                <Button
                  id='basic-button-1'
                  aria-controls={
                    Boolean(openExportType) ? 'basic-menu-2' : undefined
                  }
                  aria-haspopup='true'
                  aria-expanded={Boolean(openExportType) ? 'true' : undefined}
                  onClick={(event: any) =>
                    setOpenExportType(event.currentTarget)
                  }
                  sx={{
                    backgroundColor: '#EEEEEE',
                    border: '1px solid #DEE2E0',
                    color: 'black',
                    fontWeight: '400',
                    padding: '5px 15px',
                    borderRadius: '10px',
                    marginLeft: 1,
                    textTransform: 'capitalize',
                  }}
                  endIcon={
                    <ChevronRight
                      sx={{ transform: 'rotate(90deg)', color: '#5A525B' }}
                    />
                  }>
                  Export
                </Button>
                {selectedTab === 'Contracts' && (
                  <Button
                    sx={{
                      backgroundColor: '#F0845D',
                      color: 'white',
                      fontWeight: '400',
                      padding: '5px 15px',
                      borderRadius: '10px',
                      marginLeft: 1,
                      textTransform: 'capitalize',
                      '&:hover': {
                        backgroundColor: '#F0845D',
                      },
                    }}
                    onClick={() => navigate('/contract-ai')}
                    disabled={userRoles?.includes('superintendent')}>
                    Upload Contract
                  </Button>
                )}

                <Menu
                  id='basic-menu-2'
                  anchorEl={openExportType}
                  open={Boolean(openExportType)}
                  onClose={() => setOpenExportType(null)}
                  MenuListProps={{
                    'aria-labelledby': 'basic-button-1',
                  }}
                  sx={{
                    boxShadow: 'none',
                    '& .MuiPaper-root': {
                      boxShadow:
                        '0px 5px 12px 0px #0000000A, 0px 22px 22px 0px #00000008, 0px 49px 30px 0px #00000005, 0px 88px 35px 0px #00000003, 0px 137px 38px 0px #00000000',
                      borderRadius: '10px',
                      border: '1px solid #F1EFF5',
                    },
                    '& ul': {
                      padding: '7px 0px',
                      // backgroundColor: "red",
                      boxShadow: 'none',
                    },
                    '& li:hover': {
                      backgroundColor: 'transparent',
                    },
                    '& li': {
                      margin: '5px 17px',
                      borderBottom: '1px solid #DEE2E0',
                      textAlign: 'left',
                      padding: '5px 0',
                      fontSize: 14,
                      color: '#5F5C5F',
                    },
                    '& li:last-child': {
                      border: 'none !important',
                    },
                    marginTop: 1,
                  }}>
                  {['Export as csv'].map(item => (
                    <MenuItem
                      sx={{ width: 180 }}
                      onClick={() => {
                        setOpenExportType(null)
                        setExportAsCSV(prev => !prev)
                      }}>
                      {item}
                    </MenuItem>
                  ))}
                </Menu>

                {selectedTab === 'Daily Reports' && (
                  <Button
                    variant='contained'
                    sx={{
                      padding: '5px 15px',
                      borderRadius: '8px',
                      textTransform: 'capitalize',
                      fontSize: '13px',
                      marginLeft: 2,
                    }}
                    onClick={() => {
                      window.location.href = id + '/daily-reports/new'
                    }}>
                    Add Report
                  </Button>
                )}
                {selectedTab === 'Cost Codes' && (
                  <Button
                    variant='contained'
                    sx={{
                      padding: '5px 15px',
                      borderRadius: '8px',
                      textTransform: 'capitalize',
                      fontSize: '13px',
                      marginLeft: 2,
                    }}
                    onClick={() => navigate('cost-codes/upload')}
                    disabled={
                      userRoles === 'superintendent' || userRoles === 'worker'
                    }>
                    Upload Cost Codes
                  </Button>
                )}
                {selectedTab === 'T&M Reports' && (
                  <Button
                    variant='contained'
                    sx={{
                      padding: '5px 15px',
                      borderRadius: '8px',
                      textTransform: 'capitalize',
                      fontSize: '13px',
                      marginLeft: 2,
                    }}
                    onClick={() => navigate('time-material/new')}>
                    Add Report
                  </Button>
                )}
              </Box>
            </Box>
            <Grid container spacing={2} sx={{ mb: 3 }} alignItems='stretch'>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                {!isLoading ? (
                  selectedTab === 'T&M Reports' ? (
                    <ListTNMReports
                      keyName='projectDailyReports'
                      id={id}
                      navigate={navigate}
                      ts={ts}
                      exportAsCSV={exportAsCSV}
                      //getQuery={useGetTimeSheets}
                      //updateQuery={useMutateUpdateTimeSheet}
                      userRoles={userRoles}
                    />
                  ) : selectedTab === 'Daily Reports' ? (
                    <ListDailyReports
                      keyName='projectDailyReports'
                      id={id}
                      navigate={navigate}
                      ts={ts}
                      exportAsCSV={exportAsCSV}
                      //getQuery={useGetTimeSheets}
                      //updateQuery={useMutateUpdateTimeSheet}
                      userRoles={userRoles}
                    />
                  ) : selectedTab === 'Cost Codes' ? (
                    <CostCodesGrid
                      exportAsCSV={exportAsCSV}
                      id={id}
                      //user={user}
                      uploadPath='upload'
                      columns={costcodeColumns}
                      costCodes={costCodes?.costCodes}
                    />
                  ) : selectedTab === 'Contracts' ? (
                    <ProjectContracts
                      exportAsCSV={exportAsCSV}
                      id={id}
                      user={user}
                    />
                  ) : (
                    <ListTimesheets
                      keyName='projectTimesheets'
                      id={id}
                      navigate={navigate}
                      ts={ts}
                      exportAsCSV={exportAsCSV}
                      superintendents={data.superintendents}
                      getQuery={useGetTimeSheets}
                      updateQuery={useMutateUpdateTimeSheet}
                      userRoles={userRoles}
                      showCustomFilters={true}
                      authUser={user}
                    />
                  )
                ) : null}
              </Grid>
            </Grid>
          </Box>
        </Box>
      ) : null}
    </Box>
  )
}
